import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import './emails.css';
import {
  CELL_MODE_LOADING, CELL_MODE_FAILED, CELL_MODE_NONE, CELL_MODE_PARTIAL,
  buildDraftState,
} from '../../../utils/emailCells';
import HtmlSourceEditor from './HtmlSourceEditor';
import { isContentSafeForRichText } from '../../../utils/tiptapConfig';
import { unwrapChrome, rewrapChrome } from '../../../utils/emailCellChrome';
import { hasEditableText } from '../../../utils/emailTextRuns';
import EmailTextRunEditor from './EmailTextRunEditor';
import { RENDER_HTML_EDITORS } from './htmlEditorSurfaces';

// ProseMirror needs a DOM and /cms/:tab is server-rendered, so the editor must be kept
// out of the server bundle entirely — ssr:false, not a useEffect guard. @loadable is
// already configured in this app (browser/index.jsx calls loadableReady, server/index.jsx
// uses ChunkExtractor) and this is its first component use.
const RichTextEditor = loadable(() => import('./RichTextEditor'), {
  ssr: false,
  fallback: <div className="emails-rte-loading">Loading editor…</div>,
});

// The left column of the press check: one panel per editable cell, in document
// order, each holding the copy of a single <td> and nothing around it.
//
// This is the design's panel language unchanged - .panel > .head > <h4> + a
// .locked chip + .right with a .btn.tiny.ghost, exactly as the mockup's Header
// and Footer panels (2026-08-31-email-campaigns-mockup.html, lines 1028-1038
// and 1099-1113).
//
// THREE controls, chosen per cell, most capable first. Never chosen globally.
//
// 1. RichTextEditor (Tiptap) when isContentSafeForRichText says the cell can be
//    represented faithfully. The schema is a whitelist, so a cell holding a nested table,
//    an Outlook conditional or an image would lose exactly that markup on the first save.
//    An inline style would too, until the schema was taught to keep one — which is why
//    this is now the common case rather than the rare one: all seven cells of
//    codigo-fan-sale-es land here, where every one of them used to fall through to (2).
//
//    What the editor is given is the cell's BODY, not the cell. Every rich cell here
//    wraps its copy in one element carrying the typography — colour, family, size, line
//    height — and that wrapper is peeled off before the editor sees anything and
//    concatenated back on every change (emailCellChrome.js). It is not the copywriter's
//    to edit, the preview keeps rendering it, and a byte of it never reaches the editor.
//
// 2. EmailTextRunEditor when it cannot, but the cell still contains words. This is the
//    common case and the reason the middle control exists at all: a call-to-action is a
//    styled anchor, six characters of copy inside four hundred of chrome —
//
//      <a href="…/pro-online" style="display:inline-block;background:#000000;…">Access</a>
//
//    Dropping straight to raw HTML there asks a copywriter to edit that by hand to
//    change one word, and one stray character breaks the button in every inbox. The text
//    editor puts "Access" in a field and never shows them the anchor.
//
// 3. HtmlSourceEditor when there is no text to edit, or when the user asks for it. It is
//    an escape hatch now rather than the immediate fallback.
//
// Three things this component must never do:
//
// 1. Offer a cell that cannot be saved. The probe over all 88 stored templates
//    found 282 cells and zero identity failures - every cell the server offers
//    is one replaceCellContent will write back. buildCellPanels drops anything
//    without an id, and nothing here invents a cell the server did not send.
//
// 2. Let a short cell list imply completeness. Eleven PS templates keep their
//    copy outside any <td>, and six of them surface exactly one trivial cell
//    ("Descarga nuestra app"). Rendered bare, that panel is a claim that the
//    rest of the email is not editable - which is false; the finder simply
//    could not see it. Below 90% coverage the note above the panels says so,
//    in the same breath as offering the HTML editor. See emailCells.js for
//    where the 90% comes from (measured, not guessed).
//
// 3. Throw away copy the user typed without the user saying so. This is a tool
//    for writing email copy; losing what was written is the one failure it
//    cannot have. So the draft is per cell id (`drafts`), not one value shared
//    by the whole list: opening a second cell parks the first one's text
//    instead of overwriting it, and every panel with parked text is badged.
//    The only paths that drop a draft are Cancel and Discard, both confirmed,
//    and a save the server accepted.
//
//    The subtler half of the same rule is that a cell VANISHING from the list
//    is not permission to drop its draft either. The list is re-read after
//    every save, and it is momentarily empty while that read is in flight -
//    "not in this list" means "gone" only when the list is authoritative
//    (emailCells.js: `authoritative`, true only for a completed read). While it
//    is not, the panels keep rendering from the previous answer (REFRESHING)
//    and drafts are held as unresolved. When a list that IS authoritative comes
//    back without the cell - ids are ordinals, so a save can renumber one away -
//    the editor closes but the text is handed back in the "Unsaved copy" panel
//    rather than deleted underneath the person who typed it.
const EmailCellEditor = ({
  panel, blocked, onSave, onDirtyChange,
}) => {
  // Per cell: has the user asked to drop from the text-only editor down to raw
  // HTML? Keyed by cell id so opening a second cell does not inherit the choice.
  const [showSourceFor, setShowSourceFor] = useState({});

  const {
    mode, cells, coveragePercent, authoritative, stale,
  } = panel;

  const [openCellId, setOpenCellId] = useState(null);
  // { [cellId]: typed value }. One entry per cell the user has touched, kept
  // until it is saved or explicitly discarded.
  const [drafts, setDrafts] = useState({});
  const [savingCellId, setSavingCellId] = useState(null);

  const draftState = buildDraftState({ drafts, cells, authoritative });
  const { dirty } = draftState;
  // Drafts with no cell on screen to hang off: either really gone (orphans,
  // from an authoritative list) or not yet known (unresolved, list still being
  // read). Both are shown rather than dropped; only the wording differs.
  const homeless = draftState.orphans
    .map(item => ({ ...item, gone: true }))
    .concat(draftState.unresolved.map(item => ({ ...item, gone: false })));
  const pendingIds = draftState.pending.map(item => item.id);

  const openCell = cells.find(cell => cell.id === openCellId) || null;
  const value = openCell && typeof drafts[openCell.id] === 'string'
    ? drafts[openCell.id]
    : (openCell && openCell.innerHtml) || '';
  const openCellDirty = Boolean(openCell) && value !== openCell.innerHtml;

  useEffect(() => {
    // An open cell edit is unsaved work like any other, and the screen's
    // beforeunload guard lives one level up - it cannot see this state unless
    // it is told. Every parked draft counts, not only the open one.
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    // Cells are re-read after every save, and ids are ordinals: an edit that
    // changes content length renumbers everything after it. If the cell being
    // edited is not in the new list, the panel it belonged to is gone and the
    // editor must close rather than keep pointing at an id the server would
    // now refuse.
    //
    // Only ever from an authoritative list. The same emptiness appears for a
    // moment during every refetch, and closing on that one discards the copy
    // being typed at the time - the defect this guard used to cause rather
    // than prevent. The draft itself is kept either way; it reappears in the
    // "Unsaved copy" panel below.
    if (!authoritative) {
      return;
    }
    if (openCellId && !cells.some(cell => cell.id === openCellId)) {
      setOpenCellId(null);
    }
  }, [cells, openCellId, authoritative]);

  // THE CELL PANELS ARE NOT DRAWN ANY MORE. The block builder is the only editor,
  // and these panels — with the per-cell rich text, text-only and raw HTML
  // controls below, and the "Edit HTML instead" button that switches between the
  // last two — are the HTML way of editing the same copy. Everything below this
  // line still works; nothing on the screen reaches it. See htmlEditorSurfaces.js
  // for what it would take to put them back, and why that is one line.
  //
  // Below the hooks, not above them: an early return before useState/useEffect
  // would be a different component on every render.
  if (!RENDER_HTML_EDITORS) return null;

  const startEditing = (cell) => {
    // No discard, and no confirm to ask for: the cell being left keeps its own
    // draft, and comes back with it when it is reopened.
    setOpenCellId(cell.id);
  };

  const setDraft = (cellId, next) => {
    setDrafts(previous => ({ ...previous, [cellId]: next }));
  };

  const dropDraft = (cellId) => {
    setDrafts((previous) => {
      const next = { ...previous };
      delete next[cellId];
      return next;
    });
  };

  const closeEditor = () => {
    if (!openCell) return;
    if (openCellDirty) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        `Discard the unsaved changes to Copy ${openCell.ordinal}?\n\n`
        + 'They have not been saved and cannot be recovered.',
      );
      if (!confirmed) return;
    }
    dropDraft(openCell.id);
    setOpenCellId(null);
  };

  const discardHomeless = (item) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Discard this unsaved copy?\n\nIt was never saved and cannot be recovered.',
    );
    if (confirmed) dropDraft(item.id);
  };

  const handleSaveCell = async () => {
    if (!openCell) return;

    setSavingCellId(openCell.id);
    const saved = await onSave({ cellId: openCell.id, innerHtml: value });
    setSavingCellId(null);

    if (saved) {
      // Only on success. A refusal - a moved cell, replacement content that
      // nests a table, a variable nothing provides - wrote nothing, so the
      // editor stays open with the text the user typed still in it.
      dropDraft(openCell.id);
      setOpenCellId(null);
    }
  };

  // Rendered above whatever state the cell list is in, including the failure
  // and empty states: text the user typed must not become unreachable because
  // a refetch came back badly.
  const keptDrafts = homeless.length === 0 ? null : (
    <div className="panel keptdrafts">
      <div className="head">
        <h4>Unsaved copy</h4>
        <span className="locked dirty">
          {`${homeless.length} not saved`}
        </span>
      </div>
      <div className="body">
        <p className="hint">
          Typed here but never saved, and the cell it belongs to is not on screen right
          now. It is kept rather than discarded — copy it somewhere safe, or discard it.
        </p>
        {homeless.map(item => (
          <div className="keptdraft" key={item.id}>
            <HtmlSourceEditor
              id={`email-cell-kept-${item.id}`}
              label={item.gone
                ? `${item.id} — this cell is no longer in the list (a save can renumber cells)`
                : `${item.id} — the cell list is being re-read`}
              value={item.value}
              onChange={event => setDraft(item.id, event.target.value)}
              rows={4}
            />
            <div className="cellactions">
              <button
                type="button"
                className="btn tiny ghost"
                onClick={() => discardHomeless(item)}
              >
                Discard
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (mode === CELL_MODE_LOADING) {
    return (
      <React.Fragment>
        {keptDrafts}
        <div className="panel">
          <div className="head">
            <h4>Copy</h4>
            <span className="locked">Reading cells…</span>
          </div>
          <div className="body">
            <p className="hint">
              Working out which parts of this email can be edited as copy rather than as HTML.
            </p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  if (mode === CELL_MODE_FAILED) {
    return (
      <React.Fragment>
        {keptDrafts}
        <div className="banner bad">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#e3342f" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>This email&apos;s editable cells could not be read.</b>
            {' That is a failed request, not a statement that this email has none. '}
            {'Edit the HTML source below, or reopen this plate to try again.'}
          </span>
        </div>
      </React.Fragment>
    );
  }

  if (mode === CELL_MODE_NONE) {
    return (
      <React.Fragment>
        {keptDrafts}
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>None of this email&apos;s copy can be edited cell by cell.</b>
            {' Its text sits outside the table cells — usually written straight into a '}
            {'<table> or <tr> — so there is nothing here that can be edited on its own. '}
            {'Edit the HTML source below instead; everything else about this plate works normally.'}
          </span>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {keptDrafts}

      {mode === CELL_MODE_PARTIAL && (
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>These cells are not all of this email&apos;s copy.</b>
            {coveragePercent === null
              ? ' How much of it they cover could not be measured. '
              : ` They cover about ${Math.max(coveragePercent, 1)}% of its text. `}
            {'The rest sits outside the table cells and can only be changed in the HTML '}
            {'source below — do not read the panels here as the whole email.'}
          </span>
        </div>
      )}

      {blocked && (
        <div className="banner info">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#4c72ff" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            <b>Saving a cell is paused while the HTML source has unsaved changes.</b>
            {' A cell edit is spliced into the SAVED body on the server, so saving one now '}
            {'would apply it to a document that no longer matches what you see. Nothing '}
            {'typed here is lost — save the plate, or undo the HTML changes, and the cell '}
            {'panels below go live again.'}
          </span>
        </div>
      )}

      {cells.map((cell) => {
        const isOpen = cell.id === openCellId;
        const isSaving = cell.id === savingCellId;
        const hasDraft = pendingIds.includes(cell.id);

        return (
          <div className={isOpen ? 'panel cellpanel is-open' : 'panel cellpanel'} key={cell.id}>
            <div className="head">
              <h4>{`Copy ${cell.ordinal}`}</h4>
              <span className={hasDraft ? 'locked dirty' : 'locked'}>
                {hasDraft
                  ? 'Unsaved changes'
                  : `${cell.chars.toLocaleString()} character${cell.chars === 1 ? '' : 's'}`}
              </span>
              <div className="right">
                {isOpen ? (
                  <button
                    type="button"
                    className="btn tiny ghost"
                    onClick={closeEditor}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn tiny ghost"
                    onClick={() => startEditing(cell)}
                    disabled={blocked || Boolean(savingCellId)}
                  >
                    {hasDraft ? 'Resume' : 'Edit'}
                  </button>
                )}
              </div>
            </div>

            <div className="body">
              {isOpen ? (
                <React.Fragment>
                  {/*
                    Three controls, chosen per cell, most capable first:
                      1. rich text, when Tiptap can represent the cell faithfully;
                      2. text-only, when it cannot but the cell still has words in it —
                         a styled button is the common case, six characters of copy
                         inside four hundred of chrome;
                      3. raw HTML, only when there is no text to edit, or on request.
                    Raw HTML used to be the immediate fallback, which meant changing one
                    word on a button meant editing a styled anchor by hand.
                  */}
                  {isContentSafeForRichText(value) && !showSourceFor[cell.id] && (
                    <RichTextEditor
                      value={unwrapChrome(value).inner}
                      onChange={html => setDraft(
                        cell.id,
                        rewrapChrome({ ...unwrapChrome(value), inner: html }),
                      )}
                      disabled={blocked || Boolean(savingCellId)}
                    />
                  )}

                  {!isContentSafeForRichText(value)
                    && hasEditableText(value)
                    && !showSourceFor[cell.id] && (
                    <EmailTextRunEditor
                      id={`email-cell-${cell.id}`}
                      value={value}
                      onChange={html => setDraft(cell.id, html)}
                      disabled={blocked || Boolean(savingCellId)}
                    />
                  )}

                  {(showSourceFor[cell.id]
                    || (!isContentSafeForRichText(value) && !hasEditableText(value))) && (
                    <React.Fragment>
                      <HtmlSourceEditor
                        id={`email-cell-${cell.id}`}
                        label="Cell contents"
                        value={value}
                        onChange={event => setDraft(cell.id, event.target.value)}
                        rows={6}
                      />
                      <p className="hint emails-cell-rte-refused">
                        {'This is the cell’s raw HTML. Everything here is yours to change, '}
                        {'including the markup — which also means a stray character can '}
                        {'break how this part of the email renders.'}
                      </p>
                    </React.Fragment>
                  )}

                  {!isContentSafeForRichText(value) && hasEditableText(value) && (
                    <button
                      type="button"
                      className="btn tiny ghost emails-cell-source-toggle"
                      onClick={() => setShowSourceFor(previous => ({
                        ...previous, [cell.id]: !previous[cell.id],
                      }))}
                      disabled={blocked || Boolean(savingCellId)}
                    >
                      {showSourceFor[cell.id] ? 'Edit text only' : 'Edit HTML instead'}
                    </button>
                  )}
                  <p className="hint">
                    The contents of this one cell — the &lt;td&gt; around it is not yours to
                    edit and is left byte for byte as it is. A save here is a save: it is
                    validated and keeps a revision, exactly like saving the whole plate.
                  </p>
                  {isContentSafeForRichText(value)
                    && !showSourceFor[cell.id]
                    && Boolean(unwrapChrome(value).prefix) && (
                    <p className="hint">
                      {'This cell’s typography — its colour, font, size and line height — is set '}
                      {'on the element wrapped around the copy, and is kept exactly as it is. '}
                      {'You are editing the words and their formatting; the preview shows them '}
                      {'with that styling applied.'}
                    </p>
                  )}
                  {stale && (
                    <p className="hint">
                      <b>Saving is paused for a moment</b>
                      {' while this email’s cells are re-read after the last save — '}
                      {'a cell can be renumbered by one, and this text must not be written '}
                      {'into the wrong one. What you have typed is kept.'}
                    </p>
                  )}
                  <div className="cellactions">
                    <button
                      type="button"
                      className="btn"
                      onClick={handleSaveCell}
                      disabled={isSaving || !openCellDirty || blocked || stale}
                    >
                      {isSaving ? 'Saving…' : 'Save cell'}
                    </button>
                  </div>
                </React.Fragment>
              ) : (
                <p className="celltext">{cell.title || 'This cell holds no visible text.'}</p>
              )}
            </div>
          </div>
        );
      })}
    </React.Fragment>
  );
};

EmailCellEditor.propTypes = {
  // Straight from buildCellPanels(). Building it in the parent keeps the
  // whole-document HTML panel and these cells reading the same decision.
  panel: PropTypes.shape({
    mode: PropTypes.string.isRequired,
    cells: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      ordinal: PropTypes.number.isRequired,
      title: PropTypes.string,
      innerHtml: PropTypes.string,
      chars: PropTypes.number,
    })).isRequired,
    coveragePercent: PropTypes.number,
    // Whether an absent cell is a fact (a completed read) or just a read in
    // flight. Nothing typed may be dropped on the strength of the second.
    authoritative: PropTypes.bool,
    // The cells on screen are from the previous read while a new one runs.
    stale: PropTypes.bool,
  }).isRequired,
  blocked: PropTypes.bool,
  // Resolves true when the server accepted the edit, false on any refusal.
  onSave: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func.isRequired,
};

EmailCellEditor.defaultProps = {
  blocked: false,
};

export default EmailCellEditor;
